"use client"

import type { ElementType } from "react"

import { CarouselDots, DiscArrowUpRightIcon, Grid, Heading, Reel, Text } from "@/components/ui"
import { type ImageCrop, TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"

interface WorldTile {
  id: string
  title: string
  category?: string
  image: string
  imageCrop?: ImageCrop
  imageAlt?: string
  href?: string
  serveId?: string
}

const Tile = ({ tile, priority }: { tile: WorldTile; priority?: boolean }) => {
  const Card: ElementType = tile.href ? Link : "div"
  return (
    <Card
      {...(tile.href ? { href: tile.href } : {})}
      data-nelo-serve={tile.serveId}
      className="press group aspect-world-tile rounded-10x bg-surface-muted focus-visible:ring-brand relative block min-w-0 overflow-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
    >
      <TileImage
        src={tile.image}
        crop={tile.imageCrop}
        alt={tile.imageAlt ?? ""}
        fill
        sizes="(max-width: 576px) 88vw, (max-width: 1152px) 50vw, 420px"
        priority={priority}
        className="duration-slow ease-productive object-cover transition-transform motion-safe:group-hover:scale-105"
      />
      <span className="text-fg-inverted relative flex flex-col gap-50 p-375 pr-1600 @3xl:p-500 @3xl:pr-1600 @6xl:pr-2700">
        {tile.category && (
          <Text as="span" variant="bodyLg" family="text" tone="inverted">
            {tile.category}
          </Text>
        )}
        <Heading as="span" variant="sectionHead" family="text" tone="inverted" balance>
          {tile.title}
        </Heading>
      </span>
      {tile.href && (
        <span
          aria-hidden
          className="bg-surface-paper text-brand duration-quick ease-productive absolute top-375 right-375 grid size-7.5 place-items-center rounded-full transition-transform motion-safe:group-hover:scale-110 @3xl:top-500 @3xl:right-500 @3xl:size-10"
        >
          <DiscArrowUpRightIcon size={30} className="@3xl:size-10" />
        </span>
      )}
    </Card>
  )
}

export const WorldTiles = ({ items }: { items: WorldTile[] }) => {
  const { ref, index, stops, overflowing, goTo } = useReelPagination(items.length)
  return (
    <div className="@container">
      <div className="@6xl:hidden">
        <Reel ref={ref} gap="300" snap inset>
          {items.map((tile, tileIndex) => (
            <div key={tile.id} className="w-7/8 shrink-0 snap-start @xl:w-1/2 @2xl:w-5/12">
              <Tile tile={tile} priority={tileIndex === 0} />
            </div>
          ))}
        </Reel>
        {overflowing && (
          <div className="mt-400 flex justify-center">
            <CarouselDots count={stops} index={index} onChange={goTo} tone="plain" activeColor="bg-brand" />
          </div>
        )}
      </div>
      <Grid cols={3} gap="500" className="px-gut hidden @6xl:grid">
        {items.map((tile, tileIndex) => (
          <Tile key={tile.id} tile={tile} priority={tileIndex === 0} />
        ))}
      </Grid>
    </div>
  )
}
